import { Checkbox, InputLabel, Stack, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useContext, useMemo } from 'react';
import WarningPopup from '../../../Popups/WarningPopup';
import { useTripRateContext } from '../../TripRateContext';
import { SessionContext } from '../../../Contexts/SessionContext';
import { CounterBadge } from '../../../Utilities/CounterBadge';

const roundToTwoDecimalPlaces = (value: number) => Math.round(value * 100) / 100;
const roundDownToTwoDecimalPlaces = (value: number) => Math.floor(value * 100) / 100;
const roundUpToTwoDecimalPlaces = (value: number) => Math.ceil(value * 100) / 100;


export default function BedroomsPerDwellingRange() {
    const { surveys } = useTripRateContext();
    const { sessionState, setSessionField } = useContext(SessionContext);

    // compute min/max/avg for bedrooms per dwelling
    const minMaxParam = useMemo(() => {
        if (!Array.isArray(surveys) || surveys.length === 0) return null;

        const validValues = surveys
            .map(s => s.bedroomPerDwelling)
            .filter(v => typeof v === 'number' && !isNaN(v)) as number[];

        if (validValues.length === 0) return null;

        const min = roundDownToTwoDecimalPlaces(Math.min(...validValues));
        const max = roundUpToTwoDecimalPlaces(Math.max(...validValues));
        const avg = validValues.reduce((a, b) => a + b, 0) / validValues.length;

        setSessionField('primaryFilters.minBedroomPerDwelling', min);
        setSessionField('primaryFilters.maxBedroomPerDwelling', max);

        return { min, max, avg };
    }, [surveys]);

    const bedroomsPerDwellingChecked =
        sessionState?.primaryFilters?.bedroomPerDwellingChecked ?? true;

    const min =
        sessionState?.primaryFilters?.minBedroomPerDwelling ?? '';
    const max =
        sessionState?.primaryFilters?.maxBedroomPerDwelling ?? '';

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSessionField(
            'primaryFilters.bedroomPerDwellingChecked',
            e.target.checked
        );
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const parsed = value === '' ? '' : roundToTwoDecimalPlaces(Number(value));

        if (name === 'minBedroomPerDwelling') {
            setSessionField('primaryFilters.minBedroomPerDwelling', parsed);
        } else if (name === 'maxBedroomPerDwelling') {
            setSessionField('primaryFilters.maxBedroomPerDwelling', parsed);
        }
    };

    return (
        <Grid container component={Paper} size={12} alignItems="center" sx={{ position: 'relative' }}>
            <CounterBadge value={'6'} />

            <Grid size={12}>
                <Typography textAlign='center'>Bedrooms Per Dwelling Range<WarningPopup
                    title="Warning on Bedrooms Per Dwelling"
                    content="By default, all surveys are included. Unticking this box will restrict results to sites that have known bedroom counts per dwelling."
                    type="warning"
                /></Typography>

            </Grid>

            <Grid size={6}>
                <Stack spacing={1} direction="row" alignItems="center">
                    <InputLabel id="bedroomPerDwellingLabel">
                        All Sites Selected
                    </InputLabel>
                    <Checkbox
                        name="bedroomPerDwellingChecked"
                        checked={bedroomsPerDwellingChecked}
                        onChange={handleCheckboxChange}
                    />
                </Stack>
            </Grid>

            <Grid container size={12}>
                <Grid size={4}>
                    <Stack spacing={3}>
                        <Typography variant="body1">
                            Minimum: {minMaxParam?.min ?? '�'}
                        </Typography>
                        <Typography variant="body1">
                            Maximum: {minMaxParam?.max ?? '�'}
                        </Typography>
                    </Stack>
                </Grid>

                {!bedroomsPerDwellingChecked && (
                    <Grid size={8}>
                        <Stack spacing={1}>
                            <Grid size={12}>
                                <TextField
                                    name="minBedroomPerDwelling"
                                    value={min}
                                    onChange={handleInputChange}
                                    label="From:"
                                    type="number"
                                    inputProps={{ step: 0.01 }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    name="maxBedroomPerDwelling"
                                    value={max}
                                    onChange={handleInputChange}
                                    label="To:"
                                    type="number"
                                    inputProps={{ step: 0.01 }}
                                />
                            </Grid>
                        </Stack>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
}
